import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaCalendarAlt } from "react-icons/fa";
import ErrorPage from "../../../components/ErrorPage";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { loadSchoolYearDetails, upsertCachedSchoolYear } from "../cache";
import { updateSchoolYear } from "../api";
import { BackButton, LdsPage, StatusBadge } from "../components/Lds";
import type { AppError, SchoolYearFormValues } from "../types";
import { getYearNumber, httpStatus, toDateInput } from "../utils";
import SchoolYearForm from "./SchoolYearForm";

import { useLeaveForm } from "../../../components/navigation/BackButton";
import { useFormDirtyState, useUnsavedChangesGuard } from "../../../components/navigation/UnsavedChangesGuard";
const SchoolYearEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const leaveForm = useLeaveForm();
  const { t } = useTranslation();
  const [values, setValues] = useState<SchoolYearFormValues | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<AppError | null>(null);
  const [modal, setModal] = useState<{ show: boolean; type: string; title: string; message: string; navigateOnClose?: string }>({ show: false, type: "", title: "", message: "" });
  const { isDirty, markClean } = useFormDirtyState(values);
  const { leaveWithoutWarning } = useUnsavedChangesGuard(isDirty);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!id) return;
      try {
        const year = await loadSchoolYearDetails(id);
        if (!cancelled) {
          const loadedValues = {
            startDate: toDateInput(year.startDate),
            endDate: toDateInput(year.endDate),
            defaultMonthlyFee: year.defaultMonthlyFee ?? "",
            selfSignupDeadline: year.selfSignupDeadline ? toDateInput(year.selfSignupDeadline) : "",
          };
          setValues(loadedValues);
          markClean(loadedValues);
        }
      } catch (err) {
        if (!cancelled) {
          setError({
            message: t("years.details.load_failed", { defaultValue: "Failed to fetch school year details." }),
            status: httpStatus(err),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [id, markClean, t]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!id || !values) return;
    if (!values.startDate || !values.endDate) {
      setModal({
        show: true,
        type: "error",
        title: t("common.validation_error", { defaultValue: "Validation error" }),
        message: t("years.edit.validation_dates", { defaultValue: "Please select both start and end dates." }),
      });
      return;
    }
    setSaving(true);
    try {
      const updated = await updateSchoolYear(id, values);
      upsertCachedSchoolYear(updated);
      markClean(values);
      setModal({
        show: true,
        type: "success",
        title: t("common.success", { defaultValue: "Success" }),
        message: t("years.edit.updated", { defaultValue: "School year updated successfully." }),
        navigateOnClose: `/school/years/${id}/details`,
      });
    } catch (err) {
      setModal({
        show: true,
        type: "error",
        title: t("common.error", { defaultValue: "Error" }),
        message:
          httpStatus(err) === 403
            ? t("error.403.message", { defaultValue: "You do not have permission." })
            : t("years.edit.update_failed", { defaultValue: "Failed to update school year." }),
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorPage message={error.message} status={error.status} onRetry={() => window.location.reload()} />;
  if (!values) return null;

  return (
    <LdsPage>
      <div className="space-y-5">
        <BackButton fallback={`/school/years/${id}/details`} />
        <section className="relative overflow-hidden rounded-[28px] border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-sky-50 px-5 py-6 shadow-sm dark:border-white/10 dark:from-indigo-950/35 dark:via-slate-950 dark:to-sky-950/30 sm:px-7 sm:py-7">
          <div className="relative z-10 flex min-w-0 items-center gap-5 pr-0 lg:pr-[30%]">
            <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full border-4 border-white bg-gradient-to-br from-indigo-200 to-sky-50 text-3xl text-indigo-700 shadow-lg dark:border-slate-900 dark:from-indigo-800 dark:to-sky-950 dark:text-indigo-100 sm:h-24 sm:w-24">
              <FaCalendarAlt aria-hidden="true" />
            </div>

            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500 dark:text-slate-400">
                {t("years.edit.title", { defaultValue: "Edit school year" })}
              </p>
              <h1 className="mt-1 truncate text-3xl font-bold tracking-tight text-slate-950 dark:text-white">
                {getYearNumber(values.startDate) ?? "—"} - {getYearNumber(values.endDate) ?? "—"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                {t("years.edit.subtitle", {
                  defaultValue: "Update the dates and financial settings for this school year.",
                })}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusBadge tone="info">
                  {t("years.details.range", { defaultValue: "School year" })}
                </StatusBadge>
              </div>
            </div>
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 right-5 hidden items-center lg:flex"
            aria-hidden="true"
          >
            <FaCalendarAlt className="text-[110px] text-indigo-200/70 dark:text-indigo-800/40" />
          </div>
        </section>
        <SchoolYearForm
          values={values}
          onChange={setValues}
          onSubmit={handleSubmit}
          onCancel={() => leaveWithoutWarning(() => navigate(`/school/years/${id}/details`))}
          submitting={saving}
          submitLabel={t("years.edit.update", { defaultValue: "Update school year" })}
          showFee
        />
      </div>
      <Modal
        show={modal.show}
        type={modal.type}
        title={modal.title}
        message={modal.message}
        onClose={() => {
          setModal({ show: false, type: "", title: "", message: "" });
          if (modal.navigateOnClose) leaveForm(modal.navigateOnClose);
        }}
      />
    </LdsPage>
  );
};

export default SchoolYearEdit;
